import { stumble } from '@ipfabric/frontend-shared-assets/utils/stumble/stumble';
import type { DeviceViewSettingsTable, IntentRule } from '@ipfabric/utils';
import {
  DEVICE_ATTRIBUTE_DETAIL_TAB,
  DEVICE_DETAIL_TAB,
  INTENT_CHECK_DETAIL_TAB,
  PACKET_DETAILS_DETAIL_TAB,
  PATHLOOKUP_DEBUG_DETAIL_TAB,
  PATH_INSPECTOR_DETAIL_TAB,
  RESOURCE_HIERARCHY_DETAIL_TAB,
  STP_DETAIL_TAB,
} from 'components/DeviceView/deviceViewConstants';
import {
  type DeviceViewEntity,
  type DeviceViewSettings,
  type PathsWithName,
  isEdgeEntity,
  isLabelsEntity,
  isPacketDetailsEntity,
  isPathlookupDebugEntity,
} from 'components/DeviceView/deviceViewTypes';
import { hasEntityAppliedCustomIntentCheck } from 'components/DeviceView/helpers/hasEntityAppliedCustomIntentCheck';
import { VIRTUAL_PORTS } from 'components/DeviceView/protocolTables';
import { getDeviceViewEntityTables } from './getDeviceViewEntityTables';
import { hasNodeDetailTab } from './hasNodeDetailTab';
import { isProtocolStp } from './isProtocolStp';

const isTableWithoutEndpoint = (
  deviceViewEntity: DeviceViewEntity,
  selectedTable: string,
): boolean =>
  selectedTable === DEVICE_DETAIL_TAB ||
  selectedTable === RESOURCE_HIERARCHY_DETAIL_TAB ||
  ((deviceViewEntity.deviceViewElement === 'node' ||
    deviceViewEntity.deviceViewElement === 'labels') &&
    deviceViewEntity.labelsTabs.some(
      (tab) => tab.columnKey === selectedTable,
    )) ||
  selectedTable === DEVICE_ATTRIBUTE_DETAIL_TAB ||
  selectedTable === PATH_INSPECTOR_DETAIL_TAB;

const isValidTableSelection = (
  deviceViewEntity: DeviceViewEntity,
  availableTables: DeviceViewSettingsTable[],
  selectedTable: string,
): boolean =>
  availableTables.some(
    (table: DeviceViewSettingsTable) => table.webEndpoint === selectedTable,
  ) || isTableWithoutEndpoint(deviceViewEntity, selectedTable);

export const getSelectedTable = (
  deviceViewEntity: DeviceViewEntity,
  settings: DeviceViewSettings,
  pathsWithName: PathsWithName,
  intentRule: IntentRule | null,
  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: reasonable, long decision logic
): string | null => {
  // Path lookup entity is a special case which has a single tab that is always the selected one
  if (isPacketDetailsEntity(deviceViewEntity)) {
    return PACKET_DETAILS_DETAIL_TAB;
  }
  if (isPathlookupDebugEntity(deviceViewEntity)) {
    return PATHLOOKUP_DEBUG_DETAIL_TAB;
  }
  if (isLabelsEntity(deviceViewEntity)) {
    return (
      deviceViewEntity.labelsTabs.find(
        (tab) => tab.columnKey === deviceViewEntity.table,
      )?.columnKey ??
      deviceViewEntity.labelsTabs[0]?.columnKey ??
      null
    );
  }

  // If a table is already selected and valid, use it
  const availableTables = getDeviceViewEntityTables(
    deviceViewEntity,
    settings,
    pathsWithName,
  );
  if (
    deviceViewEntity.table !== null &&
    isValidTableSelection(
      deviceViewEntity,
      availableTables,
      deviceViewEntity.table,
    )
  ) {
    return deviceViewEntity.table;
  }

  // Show table with color filters if intent check is applied
  if (
    hasEntityAppliedCustomIntentCheck(
      deviceViewEntity.intentCheckResult,
      intentRule,
    ) &&
    (deviceViewEntity.table === null ||
      deviceViewEntity.table === INTENT_CHECK_DETAIL_TAB)
  ) {
    return INTENT_CHECK_DETAIL_TAB;
  }

  // The STP_DETAIL_TAB, unlike other edge tabs, is manually placed into separate components
  // (DeviceViewContentDetailHeader, DeviceViewContentEntity). Therefore, it's not present in
  // the availableTables list and must be returned explicitly.
  if (
    deviceViewEntity.table === STP_DETAIL_TAB &&
    isEdgeEntity(deviceViewEntity) &&
    isProtocolStp(deviceViewEntity.protocol)
  ) {
    return STP_DETAIL_TAB;
  }

  // In other cases provide a default table to open
  if (deviceViewEntity.deviceViewElement === 'node') {
    const { table } = deviceViewEntity;
    if (
      table === DEVICE_DETAIL_TAB ||
      table === RESOURCE_HIERARCHY_DETAIL_TAB ||
      table === PATH_INSPECTOR_DETAIL_TAB ||
      deviceViewEntity.labelsTabs.some((tab) => tab.columnKey === table) ||
      table === DEVICE_ATTRIBUTE_DETAIL_TAB
    ) {
      return table;
    }
    if (
      deviceViewEntity.isPathLookupNode &&
      deviceViewEntity.nodeHasPathInspectorDetail
    ) {
      return PATH_INSPECTOR_DETAIL_TAB;
    }
    if (hasNodeDetailTab(deviceViewEntity.nodeType)) {
      return DEVICE_DETAIL_TAB;
    }
    // Get default NODE table if user has not selected table yet and device has no detail tab allowed
    if (availableTables.length) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      return availableTables[0]!.webEndpoint;
    }
    return null;
  }
  if (deviceViewEntity.deviceViewElement === 'edge') {
    // Get SPT Virtual ports if user has not selected table yet
    if (isProtocolStp(deviceViewEntity.protocol)) {
      return VIRTUAL_PORTS;
    }

    // Get default EDGE table if user has not selected table yet
    if (availableTables.length) {
      // Fixed noUncheckedIndexedAccess NIM-19408
      return availableTables[0]!.webEndpoint;
    }

    // Do not return any EDGE table if available tables have not been loaded yet
    return null;
  }
  stumble('Cannot select Device Explorer default table');
  return null;
};
